import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiGet, apiPost, apiPatch, apiDelete, operationsClient } from '@/lib/api';
import { settingsEndpoints } from '@/lib/api/endpoints';
import { settingsKeys } from '@/lib/queryKeys';
import type {
  GroupListResponse,
  GroupDetail,
  CreateGroupDto,
  UpdateGroupDto,
  AddGroupMembersDto,
} from '@/types/settings';

/**
 * Invalidate every cached `userGroups(userId)` query — keyed
 * `[..., 'users', <id>, 'groups']`. Group-member mutations only carry the
 * membershipId (≠ userId), so we can't target a single key. (NOT-25 review)
 */
function invalidateAllUserGroups(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key.length >= 3 &&
        key[key.length - 1] === 'groups' &&
        key[key.length - 3] === 'users'
      );
    },
  });
}

export function useGroups() {
  return useApiQuery<GroupListResponse>({
    queryKey: settingsKeys.groups(),
    queryFn: () =>
      apiGet<GroupListResponse>(operationsClient, settingsEndpoints.groups(), {
        params: { limit: 100 },
      }),
    showErrorToast: false,
  });
}

export function useGroupDetail(groupId: string | undefined) {
  return useApiQuery<GroupDetail>({
    queryKey: settingsKeys.groupDetail(groupId ?? ''),
    queryFn: () => apiGet<GroupDetail>(operationsClient, settingsEndpoints.groupDetail(groupId!)),
    enabled: !!groupId,
    showErrorToast: false,
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useApiMutation<GroupDetail, CreateGroupDto>({
    mutationFn: (dto) =>
      apiPost<GroupDetail, CreateGroupDto>(operationsClient, settingsEndpoints.groups(), dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.groups() });
      // A create can seed memberships via `memberIds`, so refresh any cached
      // user-detail Groups card too.
      invalidateAllUserGroups(queryClient);
      toast.success('Group created');
    },
  });
}

export function useUpdateGroup() {
  const queryClient = useQueryClient();

  return useApiMutation<GroupDetail, { groupId: string } & UpdateGroupDto>({
    mutationFn: ({ groupId, ...dto }) =>
      apiPatch<GroupDetail, UpdateGroupDto>(
        operationsClient,
        settingsEndpoints.groupDetail(groupId),
        dto,
      ),
    onSuccess: (_data, { groupId }) => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.groups() });
      void queryClient.invalidateQueries({ queryKey: settingsKeys.groupDetail(groupId) });
      // A rename changes the group name shown on the user-detail Groups card.
      invalidateAllUserGroups(queryClient);
      toast.success('Group updated');
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();

  return useApiMutation<void, string>({
    mutationFn: (groupId) => apiDelete(operationsClient, settingsEndpoints.groupDetail(groupId)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.groups() });
      // A delete must clear the group from any cached user-detail Groups card.
      invalidateAllUserGroups(queryClient);
      toast.success('Group deleted');
    },
  });
}

export function useAddGroupMembers() {
  const queryClient = useQueryClient();

  return useApiMutation<GroupDetail, { groupId: string; membershipIds: string[] }>({
    mutationFn: ({ groupId, membershipIds }) =>
      apiPost<GroupDetail, AddGroupMembersDto>(
        operationsClient,
        settingsEndpoints.groupMembers(groupId),
        { membershipIds },
      ),
    onSuccess: (_data, { groupId }) => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.groups() });
      void queryClient.invalidateQueries({ queryKey: settingsKeys.groupDetail(groupId) });
      // NOT-25 review: the user-detail Groups card reads `userGroups(userId)`,
      // but member mutations only carry the membershipId (≠ userId). Invalidate
      // every cached userGroups query so no detail card shows stale membership.
      invalidateAllUserGroups(queryClient);
      toast.success('Members added');
    },
  });
}

export function useRemoveGroupMember() {
  const queryClient = useQueryClient();

  return useApiMutation<void, { groupId: string; membershipId: string }>({
    mutationFn: ({ groupId, membershipId }) =>
      apiDelete(operationsClient, settingsEndpoints.groupMember(groupId, membershipId)),
    onSuccess: (_data, { groupId }) => {
      void queryClient.invalidateQueries({ queryKey: settingsKeys.groups() });
      void queryClient.invalidateQueries({ queryKey: settingsKeys.groupDetail(groupId) });
      // NOT-25 review: refresh user-detail Groups cards (membershipId ≠ userId).
      invalidateAllUserGroups(queryClient);
      toast.success('Member removed');
    },
  });
}
